"use client";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  months,
  monthsEn,
  toNepaliNumber,
  useNepaliDatepicker,
} from "../hooks/useNepaliDatepicker";
import { type CalendarDay } from "../lib/api";
import { adToBS, bsToAD, getCurrentBSDate } from "../lib/dateConverter";
import "./NepaliCalendar.css";

export interface NepaliDatePickerProps {
  year?: number; // AD year (always AD format)
  month?: number; // AD month (always AD format, 1-12)
  type?: "icon" | "input";
  placeholder?: string;
  holidayWeekdays?: string | string[];
  showAD?: boolean;
  showBS?: boolean;
  showToggle?: boolean;
  calendarMode?: "bs" | "ad";
  showNationalHoliday?: boolean;

  className?: string;
  calendarWrapperClassName?: string;
  headerClassName?: string;
  titleClassName?: string;
  navButtonClassName?: string;
  weekdaysClassName?: string;
  weekdayClassName?: string;
  calendarDaysClassName?: string;
  calendarDayClassName?: string;
  bsDateClassName?: string;
  adDateClassName?: string;
  toggleWrapperClassName?: string;
  toggleButtonClassName?: string;

  styles?: any;

  prevArrow?: string | React.ReactNode;
  nextArrow?: string | React.ReactNode;

  weekdayLabels?: string[];

  value?: string;
  minDate?: string; // AD format date string (e.g., "2024-01-01")
  maxDate?: string; // AD format date string (e.g., "2024-12-31")
  disabled?: boolean;
  showArrows?: boolean; // Show/hide navigation arrows (default: true)
  tillCurrentYear?: boolean; // If true, show years only up to current year (for birthday selection). Default: false (shows future years too for planning)
  onDateSelect?: (date: CalendarDay) => void;
  onMonthChange?: (year: number, month: number) => void;
  onCalendarModeChange?: (mode: "bs" | "ad") => void;
}

const weekdays = ["आइत", "सोम", "मंगल", "बुध", "बिही", "शुक्र", "शनि"];
const weekdaysEn = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const POPUP_GAP = 8; // Gap between input and popup
const VIEWPORT_MARGIN = 16; // Margin from viewport edges

const NepaliDatePicker: React.FC<NepaliDatePickerProps> = ({
  year: initialYear,
  month: initialMonth,
  type = "input",
  placeholder = "Select date",
  holidayWeekdays = "Saturday",
  showToggle = false,
  calendarMode: initialCalendarMode = "bs",
  showNationalHoliday = false,
  className = "",
  calendarWrapperClassName = "",
  headerClassName = "",
  titleClassName = "",
  navButtonClassName = "",
  weekdaysClassName = "",
  weekdayClassName = "",
  calendarDaysClassName = "",
  calendarDayClassName = "",
  bsDateClassName = "",
  adDateClassName = "",
  toggleWrapperClassName = "",
  toggleButtonClassName = "",
  styles = {},
  prevArrow = "←",
  nextArrow = "→",
  weekdayLabels,
  value,
  minDate,
  maxDate,
  disabled = false,
  showArrows = true,
  tillCurrentYear = false,
  onDateSelect,
  onMonthChange,
  onCalendarModeChange,
}) => {
  const {
    currentBSYear,
    currentBSMonth,
    calendarData,
    isLoading,
    isYearLoading,
    isMonthLoading,
    isInputLoading,
    calendarMode,
    isIndexedDBInitialized,
    showMonthDropdown,
    showYearDropdown,
    selectedDate,
    selectedBSKey,
    selectedADKey,
    titleRef,
    setShowMonthDropdown,
    setShowYearDropdown,
    setSelectedDate,
    setSelectedBSKey,
    setSelectedADKey,
    getDataForKey,
    getADMonthRange,
    getDisplayMonthYear,
    getDropdownDisplayValues,
    primaryADMonth,
    primaryADYear,
    handleDayClick,
    handlePrevMonth,
    handleNextMonth,
    handleCalendarModeChange,
    handleMonthSelect,
    handleYearSelect,
    handleInputDate,
    fetchMonthData,
    fetchAllBSMonthsForADMonth,
    navigateToValuePropMonth,
  } = useNepaliDatepicker({
    year: initialYear,
    month: initialMonth,
    calendarMode: initialCalendarMode,
    value,
    onDateSelect,
    onMonthChange,
    onCalendarModeChange,
  });

  const [showCalendarPopup, setShowCalendarPopup] = useState<boolean>(false);
  const popupRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [inputValue, setInputValue] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const userTypedInputRef = useRef<string | null>(null); // Track user-typed input to prevent value prop from overwriting it
  const [adModeDays, setAdModeDays] = useState<CalendarDay[] | null>(null);
  const [popupPosition, setPopupPosition] = useState<{
    top: number;
    left: number;
    width: number;
    positionAbove: boolean;
  } | null>(null);

  // Memoize holiday index set for performance
  const holidayIndexSet = useMemo(() => {
    const normalizeHolidaySet = (input: string | string[]): Set<number> => {
      const toArray = Array.isArray(input) ? input : [input];
      const map: Record<string, number> = {
        sunday: 0,
        sun: 0,
        monday: 1,
        mon: 1,
        tuesday: 2,
        tue: 2,
        wednesday: 3,
        wed: 3,
        thursday: 4,
        thu: 4,
        friday: 5,
        fri: 5,
        saturday: 6,
        sat: 6,
      };
      const set = new Set<number>();
      for (const item of toArray) {
        const key = String(item).trim().toLowerCase();
        if (map[key] !== undefined) set.add(map[key]);
      }
      return set;
    };
    return normalizeHolidaySet(holidayWeekdays);
  }, [holidayWeekdays]);

  // Sync input value with selected date
  // IMPORTANT: value prop is always in AD format
  // Display in input based on calendarMode:
  // - calendarMode="ad": show AD date (selectedADKey from value)
  // - calendarMode="bs": show BS date (selectedBSKey, converted from value)
  useEffect(() => {
    // Don't overwrite user-typed input with value prop
    // This allows user input to override the value prop
    if (userTypedInputRef.current !== null && inputValue === userTypedInputRef.current) {
      // User just typed this value - don't overwrite it with value prop
      // Clear the ref after a short delay to allow value prop to sync later if needed
      setTimeout(() => {
        userTypedInputRef.current = null;
      }, 100);
      return;
    }

    // Handle when value prop is explicitly cleared (empty string, null)
    // If value is undefined, it means prop is not provided (uncontrolled mode)
    if (value !== undefined && (value === null || value === "")) {
      // Value prop was explicitly cleared - clear the input
      if (inputValue !== "") {
        setInputValue("");
      }
      return;
    }

    if (value) {
      // Value prop is always AD format
      // Display based on calendarMode - use selected keys which are synced with value prop
      if (calendarMode === "bs") {
        // Show BS date (converted from AD value)
        if (selectedBSKey) {
          if (selectedBSKey !== inputValue) {
            setInputValue(selectedBSKey);
          }
        } else {
          // Fallback: convert value directly if selectedBSKey not set yet
          const dateParts = value.split("-");
          if (dateParts.length === 3) {
            const adYear = parseInt(dateParts[0], 10);
            const adMonth = parseInt(dateParts[1], 10);
            const adDay = parseInt(dateParts[2], 10);
            if (!isNaN(adYear) && !isNaN(adMonth) && !isNaN(adDay)) {
              const bsDate = adToBS(adYear, adMonth, adDay);
              if (bsDate) {
                const bsDateString = `${bsDate.bs_y}-${String(
                  bsDate.bs_m
                ).padStart(2, "0")}-${String(bsDate.bs_d).padStart(2, "0")}`;
                if (bsDateString !== inputValue) {
                  setInputValue(bsDateString);
                }
              }
            }
          }
        }
      } else {
        // Show AD date (value is already AD format)
        if (selectedADKey) {
          if (selectedADKey !== inputValue) {
            setInputValue(selectedADKey);
          }
        } else if (value !== inputValue) {
          // Fallback: use value directly if selectedADKey not set yet
          setInputValue(value);
        }
      }
      return;
    }

    // No value prop (undefined) - sync with selected date (uncontrolled mode)
    if (selectedDate) {
      const newValue = calendarMode === "bs" ? selectedBSKey : selectedADKey;
      if (newValue && newValue !== inputValue) {
        setInputValue(newValue);
      }
    }
    // Note: We don't clear inputValue when selectedDate is empty to allow user typing
    // The input will be validated on blur instead
  }, [
    calendarMode,
    selectedDate,
    selectedBSKey,
    selectedADKey,
    value,
    // inputValue removed from deps to prevent clearing input while user is typing
  ]);

  // Optimized popup position calculation
  const calculatePopupPosition = useCallback(() => {
    if (!wrapperRef.current || !popupRef.current) return;

    const inputRect = wrapperRef.current.getBoundingClientRect();
    const popupHeight = popupRef.current.offsetHeight || 400; // Use actual height or fallback
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    // Calculate available space
    const spaceBelow = viewportHeight - inputRect.bottom - POPUP_GAP;
    const spaceAbove = inputRect.top - POPUP_GAP;

    // Determine position
    const positionAbove = spaceBelow < popupHeight && spaceAbove > spaceBelow;

    // Calculate top position with proper gap
    let top: number;
    if (positionAbove) {
      // Position above: bottom of popup should be POPUP_GAP above the input top
      top = inputRect.top - popupHeight - POPUP_GAP;
    } else {
      // Position below: top of popup should be POPUP_GAP below the input bottom
      top = inputRect.bottom + POPUP_GAP;
    }

    // Constrain to viewport
    top = Math.max(
      VIEWPORT_MARGIN,
      Math.min(top, viewportHeight - popupHeight - VIEWPORT_MARGIN)
    );

    // Calculate horizontal position
    const preferredWidth = 360;
    const minWidth = 320;
    const popupWidth = Math.max(
      minWidth,
      Math.min(preferredWidth, viewportWidth - VIEWPORT_MARGIN * 2)
    );

    let left = inputRect.left;

    // Adjust if overflowing right
    if (left + popupWidth > viewportWidth - VIEWPORT_MARGIN) {
      left = viewportWidth - popupWidth - VIEWPORT_MARGIN;
    }

    // Adjust if overflowing left
    if (left < VIEWPORT_MARGIN) {
      left = VIEWPORT_MARGIN;
    }

    setPopupPosition({ top, left, width: popupWidth, positionAbove });
  }, []);

  // Update position when popup opens or on scroll/resize
  useEffect(() => {
    if (showCalendarPopup) {
      // Initial calculation after a small delay to ensure popup is rendered
      const timer = setTimeout(calculatePopupPosition, 0);

      const handleUpdate = () => {
        requestAnimationFrame(calculatePopupPosition);
      };

      window.addEventListener("scroll", handleUpdate, true);
      window.addEventListener("resize", handleUpdate);

      return () => {
        clearTimeout(timer);
        window.removeEventListener("scroll", handleUpdate, true);
        window.removeEventListener("resize", handleUpdate);
      };
    } else {
      setPopupPosition(null);
    }
  }, [showCalendarPopup, calculatePopupPosition]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (titleRef.current && !titleRef.current.contains(target)) {
        setShowMonthDropdown(false);
        setShowYearDropdown(false);
      }

      if (
        popupRef.current &&
        !popupRef.current.contains(target) &&
        wrapperRef.current &&
        !wrapperRef.current.contains(target)
      ) {
        setShowCalendarPopup(false);
        setShowMonthDropdown(false);
        setShowYearDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [titleRef, setShowMonthDropdown, setShowYearDropdown]);

  // Cleanup error timeout
  useEffect(() => {
    return () => {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
    };
  }, []);

  // Fetch AD mode days
  useEffect(() => {
    if (
      calendarMode === "ad" &&
      isIndexedDBInitialized &&
      fetchAllBSMonthsForADMonth
    ) {
      let adYear: number;
      let adMonth: number;

      if (primaryADMonth !== null && primaryADYear !== null) {
        adYear = primaryADYear;
        adMonth = primaryADMonth;
      } else {
        const displayInfo = getDisplayMonthYear();
        adYear = displayInfo.yearNum;
        adMonth = displayInfo.monthNum;
      }

      if (adYear >= 1921 && adYear <= 2032 && adMonth >= 1 && adMonth <= 12) {
        fetchAllBSMonthsForADMonth(adYear, adMonth).then(setAdModeDays);
      } else {
        setAdModeDays(null);
      }
    } else {
      setAdModeDays(null);
    }
    // Remove getDisplayMonthYear from dependencies - it's causing infinite loop
  }, [
    calendarMode,
    primaryADMonth,
    primaryADYear,
    isIndexedDBInitialized,
    fetchAllBSMonthsForADMonth,
  ]);

  // Helper to check if a date (AD format) is within min/max range
  const isDateInRange = useCallback(
    (adDateString: string | undefined): boolean => {
      // If no date string, consider it out of range (disabled)
      if (!adDateString || typeof adDateString !== "string") {
        return false;
      }

      // If no restrictions, all dates are in range
      if (!minDate && !maxDate) {
        return true;
      }

      const datePattern = /^(\d{4})-(\d{2})-(\d{2})$/;

      // Validate the date string format
      const dateMatch = adDateString.match(datePattern);
      if (!dateMatch) {
        return false;
      }

      // Use string comparison for dates in YYYY-MM-DD format (lexicographically sortable)
      // This is more reliable than Date object comparison

      // Check minDate constraint
      if (minDate && typeof minDate === "string") {
        const minMatch = minDate.match(datePattern);
        if (minMatch) {
          // Compare as strings (YYYY-MM-DD format is lexicographically sortable)
          // adDateString must be >= minDate (dates before minDate are disabled)
          // Example: "2024-12-31" < "2025-01-01" → disabled
          const comparison = adDateString < minDate;
          if (comparison) {
            return false; // Date is before minDate, so it's disabled
          }
        }
      }

      // Check maxDate constraint
      if (maxDate && typeof maxDate === "string") {
        const maxMatch = maxDate.match(datePattern);
        if (maxMatch) {
          // Compare as strings (YYYY-MM-DD format is lexicographically sortable)
          // adDateString must be <= maxDate (dates after maxDate are disabled)
          // Example: "2025-11-26" > "2025-11-25" → disabled
          const comparison = adDateString > maxDate;
          if (comparison) {
            return false; // Date is after maxDate, so it's disabled
          }
        }
      }

      // Date is within range
      return true;
    },
    [minDate, maxDate]
  );

  // Handlers
  const handleDatePickerDayClick = useCallback(
    (day: CalendarDay) => {
      // Check if date is within min/max range
      if (!isDateInRange(day.ad.date)) {
        return; // Don't allow selection of disabled dates
      }
      handleDayClick(day);
      setShowMonthDropdown(false);
      setShowYearDropdown(false);
      setInputValue(calendarMode === "bs" ? day.bs.date : day.ad.date);
      // Don't close popup automatically - let user close it manually or by clicking outside
      // This ensures the selected date is visually highlighted before popup closes
    },
    [
      handleDayClick,
      calendarMode,
      setShowMonthDropdown,
      setShowYearDropdown,
      isDateInRange,
    ]
  );

  const formatInputValue = useCallback((value: string): string => {
    const digits = value.replace(/\D/g, "").slice(0, 8);
    if (digits.length <= 4) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
    return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6)}`;
  }, []);

  const validateDate = useCallback(
    (dateString: string, mode: "bs" | "ad"): boolean => {
      const datePattern = /^(\d{4})-(\d{2})-(\d{2})$/;
      const match = dateString.match(datePattern);
      if (!match) return false;

      const year = parseInt(match[1], 10);
      const month = parseInt(match[2], 10);
      const day = parseInt(match[3], 10);

      if (isNaN(year) || isNaN(month) || isNaN(day)) return false;

      // Basic range validation
      if (mode === "bs") {
        if (
          year < 1978 ||
          year > 2089 ||
          month < 1 ||
          month > 12 ||
          day < 1 ||
          day > 32
        ) {
          return false;
        }
      } else {
        if (
          year < 1921 ||
          year > 2032 ||
          month < 1 ||
          month > 12 ||
          day < 1 ||
          day > 31
        ) {
          return false;
        }
      }

      // Check min/max date range (convert to AD if needed)
      if (mode === "bs") {
        // Convert BS to AD for range checking
        const adDate = bsToAD(year, month, day);
        if (adDate) {
          const adDateString = `${adDate.ad_y}-${String(adDate.ad_m).padStart(
            2,
            "0"
          )}-${String(adDate.ad_d).padStart(2, "0")}`;
          return isDateInRange(adDateString);
        }
        return false;
      } else {
        // Already in AD format
        return isDateInRange(dateString);
      }
    },
    [isDateInRange]
  );

  const showRangeError = useCallback((mode: "bs" | "ad") => {
    // const message =
    //   mode === "bs"
    //     ? "Allowed range: BS 2033-2089"
    //     : "Allowed range: AD 1943-2033";
    const message = "Allowed range: AD 1943-2033";
    setErrorMessage(message);

    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
    }

    errorTimeoutRef.current = setTimeout(() => {
      setErrorMessage("");
      errorTimeoutRef.current = null;
    }, 4000);
  }, []);

  const isValueWithinRange = useCallback(
    (dateString: string): boolean => {
      if (!dateString) return true;
      const year = parseInt(dateString.slice(0, 4), 10);
      if (isNaN(year)) return true;
      return calendarMode === "bs"
        ? year >= 1978 && year <= 2089
        : year >= 1921 && year <= 2032;
    },
    [calendarMode]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const formatted = formatInputValue(e.target.value);
      setInputValue(formatted);
    },
    [formatInputValue]
  );

  const handleInputBlur = useCallback(async () => {
    const hasValueProp = value !== undefined && value !== null;

    if (!inputValue || inputValue.length < 10) {
      if (!hasValueProp && selectedDate) {
        setInputValue(calendarMode === "bs" ? selectedBSKey : selectedADKey);
      }
      return;
    }

    if (!validateDate(inputValue, calendarMode)) {
      showRangeError(calendarMode);
      setShowCalendarPopup(false);

      let targetBSYear: number;
      let targetBSMonth: number;

      if (calendarMode === "bs") {
        const currentBS = getCurrentBSDate();
        if (currentBS) {
          targetBSYear = currentBS.bs_y;
          targetBSMonth = currentBS.bs_m;
        } else {
          targetBSYear = currentBSYear;
          targetBSMonth = currentBSMonth;
        }
      } else {
        const today = new Date();
        const bsDate = adToBS(
          today.getFullYear(),
          today.getMonth() + 1,
          today.getDate()
        );
        if (bsDate) {
          targetBSYear = bsDate.bs_y;
          targetBSMonth = bsDate.bs_m;
        } else {
          targetBSYear = currentBSYear;
          targetBSMonth = currentBSMonth;
        }
      }

      await fetchMonthData(targetBSYear, targetBSMonth);
      handleYearSelect(targetBSYear);
      handleMonthSelect(targetBSMonth);

      if (!hasValueProp) {
        if (selectedDate) {
          setInputValue(calendarMode === "bs" ? selectedBSKey : selectedADKey);
        } else {
          setInputValue("");
        }
      }
      return;
    }

    setErrorMessage("");
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = null;
    }

    // Mark this input value as user-typed BEFORE calling handleInputDate
    // This prevents the value prop useEffect from overwriting it
    const typedValue = inputValue;
    userTypedInputRef.current = typedValue;

    const result = await handleInputDate(inputValue, calendarMode);
    
    // Update input value to reflect the selected date in current calendar mode
    // If handleInputDate succeeded (result is truthy), ensure input value is set correctly
    if (result) {
      // Explicitly set the input value to the typed value to ensure it's displayed
      // The userTypedInputRef is already set above to prevent value prop from overwriting
      if (inputValue !== typedValue) {
        setInputValue(typedValue);
      }
    } else if (!hasValueProp) {
      // If handleInputDate failed or returned null, restore previous selected date
      userTypedInputRef.current = null; // Clear user-typed ref on failure
      if (selectedDate) {
        setInputValue(calendarMode === "bs" ? selectedBSKey : selectedADKey);
      } else {
        setInputValue("");
      }
    }
  }, [
    value,
    inputValue,
    selectedDate,
    calendarMode,
    selectedBSKey,
    selectedADKey,
    validateDate,
    showRangeError,
    fetchMonthData,
    handleYearSelect,
    handleMonthSelect,
    handleInputDate,
    currentBSYear,
    currentBSMonth,
  ]);

  const handleInputKeyDown = useCallback(
    async (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        inputRef.current?.blur();
        await handleInputBlur();
      } else if (e.key === "Escape") {
        setShowCalendarPopup(false);
      } else if (e.key === "Tab") {
        // Close popup and blur input when Tab is pressed (for input type)
        // This allows natural tab navigation to next element
        setShowCalendarPopup(false);
        inputRef.current?.blur();
      }
    },
    [handleInputBlur]
  );

  const handleInputFocus = useCallback(
    async (e: React.FocusEvent<HTMLInputElement>) => {
      e.stopPropagation();
      const hasValueProp = value !== undefined && value !== null;
      const isOutOfRange = hasValueProp && !isValueWithinRange(value!);

      if (isOutOfRange) {
        setShowCalendarPopup(false);
        showRangeError(calendarMode);
        return;
      }

      // Navigate to value prop's month when popup opens (only if value prop is provided)
      if (hasValueProp && value && isIndexedDBInitialized) {
        await navigateToValuePropMonth();
      }

      setShowCalendarPopup(true);
    },
    [value, isValueWithinRange, showRangeError, calendarMode, isIndexedDBInitialized, navigateToValuePropMonth]
  );

  // Render calendar days (optimized)
  const renderCalendarDays = useCallback(() => {
    if (
      isLoading ||
      isYearLoading ||
      !isIndexedDBInitialized ||
      isMonthLoading ||
      isInputLoading
    ) {
      return (
        <div
          className="nepali-datepicker-loading-spinner-container"
          style={styles.loadingSpinner}
        >
          <div className="nepali-datepicker-loading-spinner" />
          <div
            className="nepali-datepicker-loading-text"
            style={styles.loadingText}
          >
            क्यालेन्डर डाटा लोड हुँदैछ...
          </div>
        </div>
      );
    }

    const currentData = getDataForKey(currentBSYear, currentBSMonth);
    if (!currentData) return null;

    let { days, otherData } = currentData;
    let startingWeekday = parseInt(otherData.starting_weekday.number);

    // Verify and correct "today" flag in BS mode
    const currentBS = getCurrentBSDate();
    if (currentBS) {
      const todayBSDate = `${currentBS.bs_y}-${String(currentBS.bs_m).padStart(
        2,
        "0"
      )}-${String(currentBS.bs_d).padStart(2, "0")}`;

      if (calendarMode === "bs") {
        const isCurrentMonth =
          currentBS.bs_y === currentBSYear && currentBS.bs_m === currentBSMonth;
        if (isCurrentMonth) {
          days = days.map((day) => ({
            ...day,
            today: day.bs.date === todayBSDate,
          }));
        }
      }
    }

    // Handle AD mode
    let adModeFirstDay = 1;
    if (calendarMode === "ad" && adModeDays && adModeDays.length > 0) {
      days = adModeDays;

      // Verify and correct "today" flag in AD mode (after setting days = adModeDays)
      if (currentBS) {
        const todayBSDate = `${currentBS.bs_y}-${String(
          currentBS.bs_m
        ).padStart(2, "0")}-${String(currentBS.bs_d).padStart(2, "0")}`;
        // Update days array: remove today flag from all, then set it on the correct day
        days = days.map((day) => {
          const isToday = day.bs.date === todayBSDate;
          return { ...day, today: isToday };
        });
      }
      const firstDay = days.find((day) => day.ad && day.ad.ad_d === 1);

      if (firstDay && firstDay.ad) {
        adModeFirstDay = 1;
      } else {
        const firstAvailableDay = days[0];
        if (firstAvailableDay && firstAvailableDay.ad) {
          adModeFirstDay = firstAvailableDay.ad.ad_d;
        }
      }

      const displayInfo = getDisplayMonthYear();
      const firstDayDate = new Date(
        displayInfo.yearNum,
        displayInfo.monthNum - 1,
        1
      );
      startingWeekday = firstDayDate.getDay();
    }

    const daysArray: React.ReactNode[] = [];
    const totalEmptyCells =
      calendarMode === "ad" && adModeFirstDay > 1
        ? startingWeekday + (adModeFirstDay - 1)
        : startingWeekday;

    for (let i = 0; i < totalEmptyCells; i++) {
      daysArray.push(
        <div key={`empty-${i}`} className="nepali-datepicker-day empty" />
      );
    }

    days.forEach((day, index) => {
      const weekdayIndex = (startingWeekday + index) % 7;
      const isHolidayWeekday = holidayIndexSet.has(weekdayIndex);
      const isNationalHoliday =
        showNationalHoliday &&
        day.events?.some((event) => event.national_holiday === 1);
      const isHoliday = isHolidayWeekday || isNationalHoliday;
      const isSelected =
        calendarMode === "bs"
          ? selectedBSKey === day.bs.date
          : selectedADKey === day.ad.date;
      // Check if date is disabled (must have ad.date and be outside min/max range)
      const adDate = day.ad?.date;
      // Ensure adDate is a valid string before checking range
      let isDisabled: boolean;
      if (!adDate || typeof adDate !== "string") {
        // If no valid AD date, disable the day
        isDisabled = true;
      } else {
        // Check if date is within min/max range
        // isDateInRange returns true if date is in range (enabled), false if out of range (disabled)
        isDisabled = !isDateInRange(adDate);
      }

      daysArray.push(
        <div
          key={`day-${index}`}
          className={`nepali-datepicker-day ${calendarDayClassName} ${
            isHoliday ? "holiday" : ""
          } ${day.today ? "today" : ""} ${isSelected ? "selected" : ""} ${
            isDisabled ? "disabled" : ""
          }`}
          onClick={() => !isDisabled && handleDatePickerDayClick(day)}
          style={{
            ...styles.calendarDay,
            ...(isHoliday ? styles.holiday ?? styles.saturday : {}),
            ...(isDisabled
              ? {
                  opacity: 0.4,
                  cursor: "not-allowed",
                  pointerEvents: "none",
                }
              : {}),
          }}
        >
          {calendarMode === "bs" ? (
            <>
              <div
                className={`bs-date ${bsDateClassName}`}
                style={{
                  ...styles.bsDate,
                  color:
                    isSelected || day.today
                      ? undefined
                      : isNationalHoliday
                      ? "#ef4444"
                      : styles.bsDate?.color,
                }}
              >
                {day.bs?.date_np || ""}
              </div>
              <div
                className={`ad-date ad-corner ${adDateClassName}`}
                style={{
                  position: "absolute",
                  right: "4px",
                  bottom: "4px",
                  fontSize: "8px",
                  color: isNationalHoliday ? "#ef4444" : "#111827",
                  opacity: day.today || isSelected ? 0.8 : 0.6,
                  pointerEvents: "none",
                  fontWeight: 400,
                }}
              >
                {day.ad?.ad_d ??
                  (day.ad?.date ? day.ad.date.split("-")[2] : "")}
              </div>
            </>
          ) : (
            <div
              className={`ad-date ${adDateClassName}`}
              style={{
                ...styles.adDate,
                color:
                  isSelected || day.today
                    ? undefined
                    : isNationalHoliday
                    ? "#ef4444"
                    : styles.adDate?.color,
              }}
            >
              {day.ad?.ad_d ?? (day.ad?.date ? day.ad.date.split("-")[2] : "")}
            </div>
          )}
        </div>
      );
    });

    const remainingCells = 42 - daysArray.length;
    for (let i = 0; i < remainingCells; i++) {
      daysArray.push(
        <div key={`empty-end-${i}`} className="nepali-datepicker-day empty" />
      );
    }

    return daysArray;
  }, [
    isLoading,
    isYearLoading,
    isIndexedDBInitialized,
    isMonthLoading,
    isInputLoading,
    getDataForKey,
    currentBSYear,
    currentBSMonth,
    calendarMode,
    adModeDays,
    holidayIndexSet,
    showNationalHoliday,
    selectedBSKey,
    selectedADKey,
    calendarDayClassName,
    handleDatePickerDayClick,
    styles,
    bsDateClassName,
    adDateClassName,
    getDisplayMonthYear,
    isDateInRange,
  ]);

  const renderMonths = useCallback(() => {
    const monthList = calendarMode === "bs" ? months : monthsEn;
    const { selectedMonth } = getDropdownDisplayValues();
    return monthList.map((month, index) => (
      <div
        key={index}
        className="nepali-datepicker-dropdown-item"
        onClick={() => handleMonthSelect(index + 1)}
        style={{
          padding: "8px",
          cursor: "pointer",
          backgroundColor: selectedMonth === index + 1 ? "#e8f4f8" : "white",
        }}
      >
        {month}
      </div>
    ));
  }, [calendarMode, getDropdownDisplayValues, handleMonthSelect]);

  const renderYears = useCallback(() => {
    const { selectedYear } = getDropdownDisplayValues();
    const years: number[] = [];
    if (calendarMode === "bs") {
      // BS mode: 2033 to 2089 (or up to current BS year if tillCurrentYear is true)
      const currentBSYear = getCurrentBSDate()?.bs_y || 2081;
      const endYear = tillCurrentYear ? currentBSYear : 2089;
      for (let i = 2033; i <= endYear; i++) {
        years.push(i);
      }
    } else {
      // AD mode: 1943 to 2033 (or up to current year if tillCurrentYear is true)
      const currentADYear = new Date().getFullYear();
      const endYear = tillCurrentYear ? currentADYear : 2033;
      for (let i = 1943; i <= endYear; i++) {
        years.push(i);
      }
    }
    return years.map((y) => (
      <div
        key={y}
        className="nepali-datepicker-dropdown-item"
        onClick={() => handleYearSelect(y)}
        style={{
          padding: "8px",
          cursor: "pointer",
          backgroundColor: selectedYear === y ? "#e8f4f8" : "white",
        }}
      >
        {calendarMode === "bs" ? toNepaliNumber(y) : y}
      </div>
    ));
  }, [
    calendarMode,
    getDropdownDisplayValues,
    handleYearSelect,
    tillCurrentYear,
  ]);

  const weekdaysToDisplay =
    weekdayLabels || (calendarMode === "bs" ? weekdays : weekdaysEn);

  return (
    <div ref={wrapperRef} style={{ position: "relative", width: "100%" }}>
      <div
        className="nepali-datepicker-input"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          padding: "8px 12px",
          border: type === "input" ? "1px solid #ccc" : "none",
          borderRadius: type === "input" ? "4px" : "0",
          cursor: type === "input" ? "text" : "pointer",
          justifyContent: type === "input" ? "space-between" : "flex-start",
          width: type === "input" ? "200px" : "fit-content",
          overflow: "hidden",
          boxSizing: "border-box",
          ...styles.datepickerInput,
        }}
        onClick={() => {
          if (type === "icon") {
            setShowCalendarPopup(!showCalendarPopup);
          } else if (type === "input") {
            inputRef.current?.focus();
          }
        }}
      >
        {type === "input" ? (
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onKeyDown={handleInputKeyDown}
            onFocus={handleInputFocus}
            onClick={(e) => e.stopPropagation()}
            disabled={disabled || isInputLoading}
            placeholder={placeholder}
            style={{
              border: "none",
              outline: "none",
              flex: 1,
              minWidth: 0,
              background: "transparent",
              color: inputValue ? undefined : "#999",
              cursor: isInputLoading ? "wait" : "text",
              pointerEvents: "auto",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              ...styles.datepickerInputField,
            }}
          />
        ) : null}
        {type === "icon" && (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="black"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ ...styles.datepickerIcon, float: "right" }}
          >
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        )}
        {type === "input" && (
          <span
            style={{
              marginLeft: "auto",
              fontSize: "14px",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="black"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
          </span>
        )}
      </div>

      {errorMessage && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            marginTop: "4px",
            padding: "8px 12px",
            backgroundColor: "#fee2e2",
            color: "#991b1b",
            borderRadius: "4px",
            fontSize: "12px",
            border: "1px solid #fecaca",
            zIndex: 1001,
            maxWidth: type === "input" ? "200px" : "fit-content",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
          }}
        >
          {errorMessage}
        </div>
      )}

      {showCalendarPopup &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className={`nepali-datepicker ${className} ${calendarWrapperClassName}`}
            style={{
              ...styles.calendarWrapper,
              position: "fixed",
              top: popupPosition ? `${popupPosition.top}px` : "0px",
              left: popupPosition ? `${popupPosition.left}px` : "0px",
              backgroundColor: "white",
              border: "1px solid #ccc",
              borderRadius: "8px",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              zIndex: 9999,
              width: "fit-content",
              minWidth: "360px",
              maxWidth: "fit-content",
            }}
            ref={popupRef}
          >
            {showToggle && (
              <div
                className={`nepali-datepicker-toggle-wrapper ${toggleWrapperClassName}`}
                style={styles.toggleWrapper}
              >
                <div
                  className={`nepali-datepicker-toggle-switch ${toggleButtonClassName}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCalendarModeChange(
                      calendarMode === "bs" ? "ad" : "bs"
                    );
                  }}
                  style={{
                    position: "relative",
                    width: "50px",
                    height: "24px",
                    borderRadius: "12px",
                    backgroundColor:
                      calendarMode === "bs" ? "#4169e1" : "#e5e7eb",
                    cursor: "pointer",
                    transition: "background-color 0.3s",
                    ...styles.toggleSwitch,
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: "2px",
                      left: calendarMode === "bs" ? "2px" : "26px",
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      backgroundColor: "white",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                      transition: "left 0.3s",
                      ...styles.toggleSlider,
                    }}
                  />
                </div>
                <span style={{ marginLeft: "8px", fontSize: "14px" }}>
                  {calendarMode === "bs" ? "BS" : "AD"}
                </span>
              </div>
            )}

            <div
              className={`nepali-datepicker-header ${headerClassName}`}
              style={{
                ...styles.header,
                justifyContent: showArrows ? "space-between" : "center",
              }}
            >
              {showArrows && (
                <button
                  className={`nepali-datepicker-nav-button ${navButtonClassName}`}
                  onClick={handlePrevMonth}
                  disabled={
                    isLoading ||
                    isYearLoading ||
                    isMonthLoading ||
                    isInputLoading
                  }
                  style={{
                    ...styles.navButton,
                    ...(isLoading ||
                    isYearLoading ||
                    isMonthLoading ||
                    isInputLoading
                      ? styles.navButtonDisabled
                      : {}),
                  }}
                >
                  {prevArrow}
                </button>
              )}

              <div
                ref={titleRef}
                className={`nepali-datepicker-title ${titleClassName}`}
                style={{
                  ...styles.title,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "2px",
                  cursor: "pointer",
                  position: "relative",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <div
                    onClick={() => {
                      setShowMonthDropdown(!showMonthDropdown);
                      setShowYearDropdown(false);
                    }}
                    style={{
                      backgroundColor: showMonthDropdown
                        ? "#e8f4f8"
                        : "transparent",
                      padding: "4px 8px",
                      borderRadius: "4px",
                    }}
                  >
                    {getDisplayMonthYear().month}
                  </div>
                  <span>/</span>
                  <div
                    onClick={() => {
                      setShowYearDropdown(!showYearDropdown);
                      setShowMonthDropdown(false);
                    }}
                    style={{
                      backgroundColor: showYearDropdown
                        ? "#e8f4f8"
                        : "transparent",
                      padding: "4px 8px",
                      borderRadius: "4px",
                    }}
                  >
                    {getDisplayMonthYear().year}
                  </div>
                </div>

                {calendarMode === "bs" && getADMonthRange() && (
                  <div
                    style={{
                      fontSize: "0.75rem",
                      color: "#6b7280",
                      marginLeft: "4px",
                    }}
                  >
                    {getADMonthRange()}
                  </div>
                )}

                {showMonthDropdown && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      backgroundColor: "white",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      maxHeight: "200px",
                      overflowY: "auto",
                      zIndex: 1000000,
                      width: "150px",
                      marginTop: "4px",
                    }}
                  >
                    {renderMonths()}
                  </div>
                )}
                {showYearDropdown && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      right: 0,
                      backgroundColor: "white",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      maxHeight: "200px",
                      overflowY: "auto",
                      zIndex: 1000000,
                      width: "100px",
                      marginTop: "4px",
                    }}
                  >
                    {renderYears()}
                  </div>
                )}
              </div>

              {showArrows && (
                <button
                  className={`nepali-datepicker-nav-button ${navButtonClassName}`}
                  onClick={handleNextMonth}
                  disabled={
                    isLoading ||
                    isYearLoading ||
                    isMonthLoading ||
                    isInputLoading
                  }
                  style={{
                    ...styles.navButton,
                    ...(isLoading ||
                    isYearLoading ||
                    isMonthLoading ||
                    isInputLoading
                      ? styles.navButtonDisabled
                      : {}),
                  }}
                >
                  {nextArrow}
                </button>
              )}
            </div>

            <div
              className={`nepali-datepicker-weekdays ${weekdaysClassName}`}
              style={styles.weekdays}
            >
              {weekdaysToDisplay.map((day, index) => (
                <div
                  key={index}
                  className={`nepali-datepicker-weekday ${weekdayClassName} ${
                    holidayIndexSet.has(index) ? "holiday" : ""
                  }`}
                  style={{
                    ...styles.weekday,
                    ...(holidayIndexSet.has(index)
                      ? styles.weekdayHoliday ?? styles.weekdaySaturday
                      : {}),
                  }}
                >
                  {day}
                </div>
              ))}
            </div>

            <div
              className={`nepali-datepicker-days ${calendarDaysClassName}`}
              style={styles.calendarDays}
            >
              {renderCalendarDays()}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default NepaliDatePicker;
